import {
  DEFAULT_REPORT_ITEM_FORMAT_TYPE,
  formatReportValue, getTemplateRowFormatType,
} from '~/exportReport/formatting/exportReportFormatting.helpers';
import type {
  ExportReportTableValue, ReportTableColumn,
} from '~/exportReport/table/ReportTable.model';
import type {
  ReportTemplateDataRow, ReportTemplateTableCell, ReportTemplateTableColumn,
} from '~/exportReport/template/reportTemplate';
import {
  getReportTemplateDataRowDemographicId, type ReportDemographicData,
} from '../exportReportData.helpers';

const REPORT_VALUE_NOT_AVAILABLE = {
  value: 'N/A',
  valueRaw: null,
  isError: true,
} as const;

type GetDemographicData = (columnId: string, demographicId: string) => ReportDemographicData | null;

export const getDemographicTableCellValue = ({
  column, templateRow, getDemographicData,
}: Readonly<{
  column: ReportTableColumn;
  templateRow: ReportTemplateDataRow | ReportTemplateTableCell;
  getDemographicData: GetDemographicData;
}>): ExportReportTableValue => {
  const demographicId = getReportTemplateDataRowDemographicId(templateRow);
  const demographicValue = getFetchedDemographicValue({ columnId: column.id, demographicId, getDemographicData });
  const demographicData = getDemographicData(column.id, demographicId);

  if (!demographicData) {
    return REPORT_VALUE_NOT_AVAILABLE;
  }

  const unit = templateRow.unit ?? '';
  const rawFormatType = templateRow.format_type ?? (column.type === 'custom' ? column.formatType : null);

  const formatType = rawFormatType ? getTemplateRowFormatType(rawFormatType) : DEFAULT_REPORT_ITEM_FORMAT_TYPE;
  const formattedValue = formatReportValue({ value: demographicValue, formatType, unit });

  return {
    value: formattedValue,
    valueRaw: demographicValue,
  };
};

const getFetchedDemographicValue = ({ columnId, demographicId, getDemographicData }: Readonly<{
  columnId: string;
  demographicId: string;
  getDemographicData: GetDemographicData;
}>): number | string | null => {
  const demographicData = getDemographicData(columnId, demographicId);
  if (!demographicData || demographicData.value === null) {
    return null;
  }

  return demographicData.value;
};

const normalizeExportReportColumnId = (columnId: string) => columnId.replace(/\s/g, '');

export const applyConditionalStyleToReportTableRowValues = ({
  tableRowValues,
  columns,
}: {
  tableRowValues: ReadonlyArray<ExportReportTableValue>;
  columns: ReadonlyArray<ReportTemplateTableColumn>;
}): ExportReportTableValue[] => {
  const getValueById = (id: string) => {
    const normalizedId = normalizeExportReportColumnId(id);

    const columnIndex = columns.findIndex(column => normalizeExportReportColumnId(column?.id ?? '') === normalizedId);
    if (columnIndex === -1) {
      return null;
    }

    return tableRowValues[columnIndex]?.valueRaw ?? null;
  };

  return tableRowValues.map((value, cellIndex) => {
    const column = columns[cellIndex];
    if (!column?.conditional_style) {
      return value;
    }

    for (const item of column.conditional_style) {
      if (typeof item.condition !== 'string' || !item.condition.trim()) {
        continue;
      }

      // identify operator and arguments
      const match = /^([\w\s.\-_]+)\s*([<>=!]+)\s*(.+)$/.exec(item.condition);
      if (match) {
        const leftId = normalizeExportReportColumnId(match[1]?.trim() ?? '');
        const operator = match[2];
        const rightId = normalizeExportReportColumnId(match[3]?.trim() ?? '');

        const leftValue = getValueById(leftId);
        const rightValue = getValueById(rightId);

        if (leftValue !== null && rightValue !== null) {
          const leftNumber = Number(leftValue);
          const rightNumber = Number(rightValue);

          let isMatch = false;
          switch (operator) {
            case '<': isMatch = leftNumber < rightNumber; break;
            case '>': isMatch = leftNumber > rightNumber; break;
            case '<=': isMatch = leftNumber <= rightNumber; break;
            case '>=': isMatch = leftNumber >= rightNumber; break;
            case '==': isMatch = leftValue === rightValue; break;
            case '!=': isMatch = leftValue !== rightValue; break;
            default: break;
          }

          if (isMatch) {
            return { ...value, color: item.color };
          }
        }
      }
    }

    return value;
  });
};
